import { useState } from 'react';
import { useCurrentUser } from '../../contexts/UserContext';
import Button from '../shared/Button';
import { TEXT } from '../shared/Typography';

const API_BASE = import.meta.env.VITE_API_BASE;

interface RequestAccessProps {
  onLogout: () => void;
}

type Status = 'sent' | 'error' | null;

export default function RequestAccess({ onLogout }: RequestAccessProps) {
  const currentUser = useCurrentUser();
  const [status, setStatus] = useState<Status>(null);

  const requestAccess = async (): Promise<void> => {
    try {
      const token = currentUser?.token;
      const res = await fetch(`${API_BASE}/auth/request-access`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setStatus('sent');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white shadow-md rounded-lg max-w-md w-full overflow-hidden">
          <div className="bg-blue-50 px-8 py-8">
            <div className="flex justify-center">
              <img src="/grautomates-dark.png" alt="GRAutomates" className="h-24 w-auto object-contain" />
            </div>
          </div>
          <div className="px-8 py-6 text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className={`mb-2 ${TEXT.titleLg}`}>Request Submitted</h2>
            <p className={`mb-6 ${TEXT.bodyMuted}`}>
              Your access request has been submitted successfully. An email has been sent to the
              helpdesk team and you will receive an email once your account is activated.
            </p>
            <Button variant="primary" onClick={onLogout} fullWidth>
              Return to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white shadow-md rounded-lg max-w-md w-full overflow-hidden">
          <div className="bg-blue-50 px-8 py-8">
            <div className="flex justify-center">
              <img src="/grautomates-dark.png" alt="GRAutomates" className="h-24 w-auto object-contain" />
            </div>
          </div>
          <div className="px-8 py-6 text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className={`mb-2 ${TEXT.titleLg}`}>Request Failed</h2>
            <p className={`mb-6 ${TEXT.bodyMuted}`}>
              We couldn&apos;t send your access request. Please try again or contact
              helpdesk@grps.com.au directly.
            </p>
            <div className="flex gap-3">
              <Button variant="primary" onClick={() => setStatus(null)} className="flex-1">
                Try Again
              </Button>
              <Button variant="outline" onClick={onLogout} className="flex-1">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white shadow-md rounded-lg max-w-md w-full overflow-hidden">
        <div className="bg-blue-50 px-8 py-8">
          <div className="flex justify-center">
            <img src="/grautomates-dark.png" alt="GRAutomates" className="h-24 w-auto object-contain" />
          </div>
        </div>
        <div className="px-8 py-6 text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className={`mb-2 ${TEXT.titleLg}`}>Request Access</h2>
          <p className={`mb-4 ${TEXT.bodyMuted}`}>
            This is your first time using this system. Click the button below to request access
            and an admin will action your request.
          </p>
          <p className={`mb-6 ${TEXT.caption}`}>
            Any issues? Contact{' '}
            <a href="mailto:helpdesk@grps.com.au" className="text-blue-600 hover:underline">
              helpdesk@grps.com.au
            </a>
          </p>
          <Button variant="primary" onClick={requestAccess} fullWidth className="mb-3">
            Request Access
          </Button>
          <Button variant="linkMuted" size="sm" onClick={onLogout}>
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
}
